import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import '@/lib/chartjs-register';
import { convertChartOptions } from '@/lib/chartjs-register';
import { ChartWidget } from '@/components/ui/shared/ChartWidget';
import ColorJson from '../../src/whitelabel.json';

/**
 * MultiLineChart - Functional component for Multi-line/List toggle widget
 * 
 * Replaces the class-based MultiLineList component with modern React patterns
 * Supports line charts with multiple datasets
 */
function MultiLineChart({ data, _Index }) {
    // State - only show loader if data is ready to fetch
    const [chartData, setChartData] = useState(null);
    const [listData, setListData] = useState([]);
    const [listHeaders, setListHeaders] = useState([]);
    const [legendItems, setLegendItems] = useState([]);
    const [sumOfLegendValue, setSumOfLegendValue] = useState('');
    const [isLoading, setIsLoading] = useState(!!(data?.RequestJson && data?.url));
    const [error, setError] = useState(null);
    const [noDataMessage, setNoDataMessage] = useState('');

    // Refs
    const abortControllerRef = useRef(null);
    const prevRequestJsonRef = useRef(null);

    // Determine if RxHacker theme
    const isRxHacker = useMemo(() => {
        const host = window.location.host.split('.').slice(-2).join('.').toLowerCase();
        return host === "rxhacker.com" || 
               host === "localhost:5464" || 
               window.location.host.toLowerCase() === "tempfilter.23byt.es" || 
               window.location.host.toLowerCase() === "reporting.nulifespanrx.com";
    }, []);

    // Get theme colors
    const themeColors = useMemo(() => {
        if (isRxHacker) {
            return {
                primary: ColorJson.ApplicationColor.Rxhacker_primary_color,
                secondary: ColorJson.ApplicationColor.Rxhacker_secondary_color,
                extra: ColorJson.ApplicationColor.Rxhacker_extra_color,
            };
        }
        return {
            primary: ColorJson.ApplicationColor.Glic_primary_color,
            secondary: ColorJson.ApplicationColor.Glic_secondary_color,
            extra: ColorJson.ApplicationColor.Glic_extra_color,
        };
    }, [isRxHacker]);

    // Handle API response
    const handleResponse = useCallback((response) => {
        return response.text().then(text => {
            try {
                return text && JSON.parse(text);
            } catch {
                return [];
            }
        });
    }, []);

    // Export widget data as CSV
    const handleExport = useCallback(() => {
        try {
            const widgetTitle = data.title || 'export';
            const tableData = [];
            
            if (listHeaders.length > 0) {
                tableData.push(listHeaders.join(','));
            }
            
            listData.forEach(row => {
                const rowData = row.map((cell, cellIndex) => {
                    // Explicitly convert to string first to handle any data type
                    let cellValue = String(cell || '');
                    
                    // Remove any commas from the value
                    cellValue = cellValue.split(',').join('');
                    
                    // Check if this looks like a date in format XX/XX or XX/XX/XXXX
                    // Add a leading space to prevent Excel from auto-formatting dates
                    if (cellIndex === 0 && /^\d{1,2}\/\d{1,2}(\/\d{2,4})?$/.test(cellValue)) {
                        cellValue = ` ${cellValue}`;
                    }
                    
                    return cellValue;
                });
                tableData.push(rowData.join(','));
            });
            
            const csvContent = tableData.join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `${widgetTitle}.csv`;
            link.href = url;
            document.body.appendChild(link);
            
            setTimeout(() => {
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }, 0);
        } catch (err) {

        }
    }, [data.title, listData, listHeaders]);

    // Build chart options
    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false, // Hide legend completely
            },
            tooltip: {
                enabled: true,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#1d252d',
                bodyColor: '#1d252d',
                borderColor: '#e2e8f0',
                borderWidth: 1,
                padding: 12,
                cornerRadius: 8,
                titleFont: {
                    size: 12,
                    weight: 'normal',
                },
                bodyFont: {
                    size: 13,
                    weight: 'bold',
                },
            },
        },
        scales: {
            x: {
                border: {
                    display: false,
                },
                grid: {
                    display: false,
                    drawBorder: false,
                },
                ticks: {
                    display: false,
                    color: '#94a3b8',
                    font: {
                        size: 11,
                    },
                },
                title: {
                    display: false,
                },
            },
            y: {
                beginAtZero: true,
                border: {
                    display: false,
                },
                grid: {
                    display: true,
                    drawBorder: false,
                    color: 'rgba(0, 0, 0, 0.05)',
                },
                ticks: {
                    color: '#94a3b8',
                    font: {
                        size: 11,
                    },
                },
                title: {
                    display: !!data.YlabelString,
                    text: data.YlabelString || '',
                    color: '#94a3b8',
                    font: {
                        size: 12,
                        weight: 'normal',
                    },
                },
            },
        },
        elements: {
            line: {
                tension: 0.3,
                borderWidth: 2,
            },
            point: {
                radius: 3,
                hoverRadius: 5,
            },
        },
        interaction: {
            intersect: false,
            mode: 'index',
        },
    }), [data.YlabelString]);

    // Fetch data
    const fetchData = useCallback(async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsLoading(true);
        setError(null);
        setNoDataMessage('Loading. Please wait.');

        try {
            const response = await fetch(data.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'requestHeader': data.RequestJson1,
                    'X-Browser-Url': window.location.href,
                },
                body: data.RequestJson,
                signal: controller.signal,
            });

            const apidata = await handleResponse(response);

            if (controller.signal.aborted) return;

            if (!apidata || !apidata[0]) {
                setNoDataMessage('No data available');
                setIsLoading(false);
                return;
            }

            // Process legend data
            try {
                const legends = [];
                if (apidata[0].legend?.length > 0) {
                    apidata[0].legend.forEach((leg, idx) => {
                        legends.push({
                            text: leg.legendText,
                            value: leg.legendValue,
                            color: idx === 0 ? themeColors.secondary : 
                                   idx === 1 ? themeColors.primary : 
                                   themeColors.extra,
                        });
                    });
                }
                setLegendItems(legends);
                
                if (apidata[0].sumOfLegendValues?.[0]?.sumOfLegendValue) {
                    setSumOfLegendValue(apidata[0].sumOfLegendValues[0].sumOfLegendValue);
                }
            } catch {
                setLegendItems([]);
            }

            // Process chart data
            try {
                // Check for lineDataSet (no number), lineDataSet1, lineDataSet2, lineDataSet3
                const lineDataSet = apidata[0].lineDataSet?.[0] || apidata[0].lineDataSet;
                const lineDataSet1 = apidata[0].lineDataSet1?.[0] || apidata[0].columnDataSet1?.[0] || apidata[0].lineDataSet1 || apidata[0].columnDataSet1;
                const lineDataSet2 = apidata[0].lineDataSet2?.[0] || apidata[0].columnDataSet2?.[0] || apidata[0].lineDataSet2 || apidata[0].columnDataSet2;
                const lineDataSet3 = apidata[0].lineDataSet3?.[0] || apidata[0].columnDataSet3?.[0] || apidata[0].lineDataSet3 || apidata[0].columnDataSet3;

                if (lineDataSet || lineDataSet1 || lineDataSet2 || lineDataSet3) {
                    const datasets = [];
                    let chartLabels = [];

                    // Helper function to extract data and labels
                    const extractDataset = (dataset, color, bgColor, fallbackLabel) => {
                        if (!dataset) return null;
                        
                        // Handle both structures: direct data/labels or nested datasets array
                        let data, labels, label;
                        
                        if (dataset.datasets && Array.isArray(dataset.datasets)) {
                            // Check if it uses x/y structure (e.g., {x: "01/01", y: 870})
                            if (dataset.datasets[0]?.x !== undefined && dataset.datasets[0]?.y !== undefined) {
                                // Extract x as labels and y as data, excluding "Total Transactions"
                                const filtered = dataset.datasets.filter(d => d.x !== 'Total Transactions');
                                labels = filtered.map(d => d.x);
                                data = filtered.map(d => parseFloat(d.y) || 0);
                                label = dataset.toplabel || fallbackLabel;
                            } else if (dataset.datasets[0]?.data !== undefined) {
                                // Old structure with data/label properties
                                data = dataset.datasets.map(d => parseFloat(d.data) || 0);
                                labels = dataset.datasets.map(d => d.label || '');
                                label = dataset.toplabel || fallbackLabel;
                            }
                        } else if (dataset.data && Array.isArray(dataset.data)) {
                            // Direct data/labels structure
                            data = dataset.data;
                            labels = dataset.labels || [];
                            label = dataset.toplabel || dataset.label || fallbackLabel;
                        }

                        if (data && data.length > 0) {
                            if (labels.length > 0 && chartLabels.length === 0) {
                                chartLabels = labels;
                            }
                            return {
                                label: label,
                                data: data,
                                borderColor: color,
                                backgroundColor: color,
                                fill: false,
                                tension: 0.4,
                            };
                        }
                        return null;
                    };

                    // Try to extract all possible datasets
                    const ds0 = extractDataset(lineDataSet, themeColors.secondary, `${themeColors.secondary}20`, 'Line');
                    const ds1 = extractDataset(lineDataSet1, themeColors.secondary, `${themeColors.secondary}20`, 'Line 1');
                    const ds2 = extractDataset(lineDataSet2, themeColors.primary, `${themeColors.primary}20`, 'Line 2');
                    const ds3 = extractDataset(lineDataSet3, themeColors.extra, `${themeColors.extra}20`, 'Line 3');

                    if (ds0) datasets.push(ds0);
                    if (ds1) datasets.push(ds1);
                    if (ds2) datasets.push(ds2);
                    if (ds3) datasets.push(ds3);

                    if (datasets.length > 0) {
                        setChartData({
                            labels: chartLabels,
                            datasets,
                        });
                        setNoDataMessage('');
                    } else {

                        setNoDataMessage('No Data Available');
                    }
                } else {

                    setNoDataMessage('No Data Available');
                }
            } catch (err) {

                setNoDataMessage('Error processing data');
            }

            // Process list data
            try {
                const listDataSet = apidata[0].listDataSet;
                if (listDataSet && listDataSet.length > 0) {
                    const headers = listDataSet[0]?.datasets?.map(d => d.data) || [];
                    setListHeaders(headers);

                    const rows = listDataSet.slice(1).map(row => 
                        row.datasets?.map(d => d.data) || []
                    );
                    setListData(rows);
                }
            } catch (err) {

            }

            setIsLoading(false);
        } catch (err) {
            if (err.name === 'AbortError') return;

            setError('Error loading data');
            setIsLoading(false);
        }
    }, [data.url, data.RequestJson, data.RequestJson1, handleResponse, themeColors]);

    // Fetch data when RequestJson changes (with null guard)
    useEffect(() => {
        if (data.type === 'MultiLine_List' && data.RequestJson && data.url && 
            data.RequestJson !== prevRequestJsonRef.current) {
            prevRequestJsonRef.current = data.RequestJson;
            fetchData();
        }
    }, [data.type, data.RequestJson, data.url, fetchData]);

    // Initial fetch (with null guard and proper deps)
    useEffect(() => {
        if (data.type === 'MultiLine_List' && data.RequestJson && data.url) {
            fetchData();
        }
        
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [data.type, data.RequestJson, data.url, fetchData]);

    // Format legend value
    const formattedLegendValue = useMemo(() => {
        if (!sumOfLegendValue) return '';
        
        const showDollar = data.title !== 'Client Access Link Signups' && 
                          data.title !== 'New vs Repeat Transactions';
        
        return showDollar ? `$${sumOfLegendValue}` : sumOfLegendValue;
    }, [sumOfLegendValue, data.title]);

    // Render chart content
    const chartContent = useMemo(() => {
        if (!chartData || !chartData.labels?.length) {
            return null;
        }

        return (
            <div className="h-full w-full flex flex-col">
                {/* Legend items */}
                {legendItems.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-4 mb-4 text-sm">
                        {legendItems.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                                <span 
                                    className="w-3 h-3 rounded-full" 
                                    style={{ backgroundColor: item.color }}
                                />
                                <span className="font-semibold">{item.value}</span>
                                <span className="text-muted-foreground">{item.text}</span>
                            </div>
                        ))}
                    </div>
                )}
                
                {/* Chart */}
                <div className="flex-1 min-h-0">
                    <Line 
                        data={chartData} 
                        options={convertChartOptions(chartOptions)} 
                    />
                </div>
            </div>
        );
    }, [chartData, chartOptions, legendItems]);

    return (
        <ChartWidget
            title={data.title}
            subtitle={data.Subtitle}
            legendValue={formattedLegendValue}
            isLoading={isLoading}
            error={error}
            noDataMessage={noDataMessage || 'No data available'}
            chartContent={chartContent}
            chartType="Line"
            listData={listData}
            listHeaders={listHeaders}
            onExport={handleExport}
            exportTitle={data.title}
            showToggle={true}
            defaultView="chart"
            minHeight="300px"
        />
    );
}

export default MultiLineChart;

